import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'convex/react';
import { Dialog, DialogBody, DialogTitle, DialogActions } from '../../catalyst-components/dialog';
import { Field, Label } from '../../catalyst-components/fieldset';
import { Select } from '../../catalyst-components/select';
import { Input } from '../../catalyst-components/input';
import { Textarea } from '../../catalyst-components/textarea';
import { Button } from '../../catalyst-components/button';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../catalyst-components/alert';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import { BaseContext } from '../base-context';

type FeedbackType = 'bug' | 'feature_request' | 'general';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { convexUserId } = useContext(BaseContext);
  const submitFeedback = useMutation(api.feedback.submit);

  const [type, setType] = useState<FeedbackType>('general');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setType('general');
    setSubject('');
    setMessage('');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim() || !message.trim()) {
      setError(t('feedback.errorRequiredFields'));
      return;
    }

    if (!convexUserId) {
      setError(t('feedback.errorLoginRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await submitFeedback({
        user_id: convexUserId as Id<"users">,
        type,
        subject: subject.trim(),
        message: message.trim(),
      });

      // Close dialog immediately, reset form after it's gone
      onClose();
      setTimeout(() => resetForm(), 300);
      // Show success alert after dialog backdrop fully transitions out
      setTimeout(() => setShowSuccess(true), 400);
    } catch (err) {
      setError(t('feedback.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} size="lg">
        <DialogBody>
          <DialogTitle>{t('feedback.dialogTitle')}</DialogTitle>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('feedback.dialogDescription')}
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <Field>
              <Label>{t('feedback.typeLabel')}</Label>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value as FeedbackType)}
              >
                <option value="general">{t('feedback.typeGeneral')}</option>
                <option value="bug">{t('feedback.typeBug')}</option>
                <option value="feature_request">{t('feedback.typeFeatureRequest')}</option>
              </Select>
            </Field>

            <Field>
              <Label>{t('feedback.subjectLabel')}</Label>
              <Input
                required
                placeholder={t('feedback.subjectPlaceholder')}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </Field>

            <Field>
              <Label>{t('feedback.messageLabel')}</Label>
              <Textarea
                required
                rows={4}
                placeholder={t('feedback.messagePlaceholder')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </Field>

            {error && (
              <p className="text-sm text-red-500">{error}</p>
            )}

            <DialogActions>
              <Button color="light" onClick={handleClose} disabled={isSubmitting}>
                {t('common.cancel')}
              </Button>
              <Button color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('common.sending') : t('common.send')}
              </Button>
            </DialogActions>
          </form>
        </DialogBody>
      </Dialog>

      <Alert open={showSuccess} onClose={() => setShowSuccess(false)} size="md">
        <AlertTitle>{t('feedback.successTitle')}</AlertTitle>
        <AlertDescription>{t('feedback.successMessage')}</AlertDescription>
        <AlertActions>
          <Button plain onClick={() => setShowSuccess(false)}>{t('common.close')}</Button>
        </AlertActions>
      </Alert>
    </>
  );
};
